<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import Loading from '~/components/loading/index.vue'
import env from '~/env'
import useSearchParams from '~/hooks/useSearchParams'
// services
import keycloakApi from '~/services-v2/keycloack-api/keycloack-api'
import { startSilentRefresh } from '~/services-v2/keycloack-api/keycloack-api.silent-refresh'
import useLogout from '~/store-v2/auth/useLogout'
import { getPartnerId, setUserProperties } from '~/utils/analytics'
import KnowError from './exceptions/KnownError'
import useAuth from './store-v2/auth/useAuth'
import useLogin from './store-v2/auth/useLogin'
import useUser from './store-v2/user/useUser'

const { mutateAsync: logout } = useLogout()
useHead({
  title: 'Financiamentos',
  meta: [
    { name: 'description', content: 'Opinionated Vite Starter Template' },
    {
      name: 'theme-color',
      content: computed(() => isDark.value ? '#00aba9' : '#ffffff'),
    },
  ],
  link: [
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: computed(() => preferredDark.value ? '/favicon-dark.svg' : '/favicon.svg'),
    },
  ],
})
const route = useRoute<any>()
const [searchParams] = useSearchParams()

const code = searchParams.value.get('code') as string | undefined

const {
  needRefresh,
  updateServiceWorker,
} = useRegisterSW()

startSilentRefresh()

const {
  data: authData,
  isError: isAuthError,
  isLoading: isAuthLoading,
  refetch: refetchAuth,
  error: authErr,
} = useAuth()

watch(authData, (auth) => {
  if (!auth)
    return

  const person = auth.person
  const me = auth.me
  const profile = person?.profile

  setUserProperties({
    userId: person?.id?.toString(),
    partnerId: getPartnerId(me, person),
    profile,
    userType: profile,
    isAdmin: me?.perfil?.toLowerCase() === 'admin',
  })
}, { immediate: true })

const {
  isError: isLoginError,
  isPending: isLoginPending,
  mutateAsync: handleLogin,
} = useLogin()

async function handleAuthError() {
  if (!code) // if there is no authorization code, attempt to execute a redirect
    return keycloakApi.redirect()

  // if there an code, attempt to handle login
  await handleLogin(code)
    .catch(keycloakApi.redirect) // If there is an error, attempt to execute a redirect
    .finally(keycloakApi.cleanUrlParams) // Clean the URL params
}

async function retry() {
  await updateServiceWorker()
  await refetchAuth()
}

watch([isAuthLoading, isAuthError], async () => {
  if (isAuthLoading.value)
    return

  if (isAuthError.value && authErr.value instanceof KnowError)
    handleAuthError()
})

const { data: user } = useUser()

watch(() => user.value, (_user) => {
  if (!_user || !_user.terms?.operating_agreement)
    return

  const { is_current_term_accepted } = _user.terms?.operating_agreement

  if (is_current_term_accepted)
    return

  window.location.href
    = `${env.VITE_INTEGRATOR_PORTAL_URL}/term?redirect=${env.VITE_FINANCIAL_URL}${route.fullPath}`
})
</script>

<template>
  <VueQueryDevtools />
  <div v-if="needRefresh" class="centered">
    <Loading />
    <p>Atualizando app...</p>
  </div>

  <div v-if="isAuthLoading" class="centered">
    <Loading />
  </div>

  <div v-else-if="isAuthError || isLoginError" class="centered">
    <div v-if="(authErr instanceof KnowError)">
      <Loading />
      <p>Aguarde. Redirecionando...</p>
    </div>
    <div v-else class="centered">
      <img src="/images/logo-solfacil_base.svg" alt="Logo Solfácil" class="mb-4xs">
      <strong>Estamos com problemas técnicos</strong>
      <small v-if="isAuthError">
        Erro na autenticação do usuario
      </small>
      <small v-if="isLoginError">
        Erro ao realizar o login
      </small>
      <small>
        Código: {{ authErr?.name }}
      </small>
      <div class="flex">
        <SolButton v-if="isAuthError" id="logout" size="small" class="m-4xs" @click="logout">
          Logout
        </SolButton>
        <SolButton v-else id="retry" size="medium" @click="retry">
          Tentar novamente
        </SolButton>
      </div>
    </div>
  </div>

  <div v-else-if="isLoginPending" class="centered">
    <Loading />
    <p>Autenticando...</p>
  </div>

  <div v-else>
    <RouterView />
  </div>
</template>

<style scoped>
  .centered {
    display: flex;
    place-items: center;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100dvh;
    text-align: center;
    background-color: white;
  }
</style>
